import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { stripeService } from '../services/stripe';
import { SUBSCRIPTION_PLANS, SubscriptionTier } from '../types/user';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);

  const handleSubscribe = async (tier: SubscriptionTier) => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      toast.error('Please sign in to subscribe');
      // Store the intended subscription tier in sessionStorage for after login
      sessionStorage.setItem('pendingSubscription', tier);
      navigate('/signin');
      return;
    }

    setLoading(tier);
    try {
      const successUrl = `${window.location.origin}/subscription/success`;
      const cancelUrl = `${window.location.origin}/subscription`;

      await stripeService.createCheckoutSession(
        tier,
        user.id,
        successUrl,
        cancelUrl
      );
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to start subscription process');
      setLoading(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-gray-900">Subscription Plans</h2>
        <p className="mt-4 text-lg text-gray-600">
          Choose the plan that works best for your security intelligence needs
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {Object.entries(SUBSCRIPTION_PLANS).map(([key, plan]) => (
          <div
            key={key}
            className={`relative bg-white rounded-lg shadow-lg overflow-hidden ${
              key === 'platinum' ? 'ring-2 ring-blue-500' : ''
            }`}
          >
            {key === 'platinum' && (
              <div className="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 text-xs font-bold">
                POPULAR
              </div>
            )}
            <div className="p-6">
              <h3 className="text-2xl font-bold text-gray-900 capitalize">{plan.tier}</h3>
              <p className="mt-4">
                <span className="text-4xl font-extrabold text-gray-900">
                  ${plan.price}
                </span>
                <span className="text-base font-medium text-gray-500">/month</span>
              </p>
              <ul className="mt-6 space-y-4">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleSubscribe(key as SubscriptionTier)}
                disabled={loading !== null}
                className={`mt-8 w-full px-4 py-2 rounded-md font-medium transition-colors ${
                  loading === key
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                } disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed`}
              >
                {loading === key ? 'Processing...' : 'Subscribe Now'}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center">
        <p className="text-sm text-gray-500">
          All plans include access to real-time security intelligence data worldwide
        </p>
        <p className="mt-2 text-xs text-gray-400">
          Secure payment processing powered by Stripe
        </p>
        
        {/* Legal Links */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 mb-2">
            By subscribing, you agree to our{' '}
            <Link to="/terms" className="text-blue-600 hover:text-blue-500 underline">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-blue-600 hover:text-blue-500 underline">
              Privacy Policy
            </Link>
          </p>
          <p className="text-xs text-gray-500">
            Subscriptions auto-renew monthly. Cancel anytime.{' '}
            <Link to="/terms#refund" className="text-blue-600 hover:text-blue-500 underline">
              Refund Policy
            </Link>{' '}
            |{' '}
            <Link to="/contact" className="text-blue-600 hover:text-blue-500 underline">
              Contact Support
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
